/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Effect, FullAssignment } from "../../utils/Interfaces";
import { NavigateFunction, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import { CardActionArea, IconButton, Link, Tooltip, Typography } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";

import DirectionsIcon from "@mui/icons-material/Directions";
import FlagIcon from "@mui/icons-material/Flag";
import { getGoogleMapsURL } from "../../utils/Functions";
import InfoModal from "../../components/modal/InfoModal";

export interface TechnicianAssignmentCardProps {
    assignment: FullAssignment;
    allowTicketSelect: boolean;
    disableTicketSelect: Effect;
    enableTicketSelect: Effect;
}

const TechnicianAssignmentCard = (props: TechnicianAssignmentCardProps) => {
    const { assignment, allowTicketSelect, disableTicketSelect, enableTicketSelect } = props;

    const [siteCommentsModalOpen, setSiteCommentsModalOpen] = useState(false);

    const { technician_entry } = assignment;

    const { work_order, comments } = technician_entry;

    const { site } = work_order;

    const navigate: NavigateFunction = useNavigate();

    if (!site) return null;

    const { store_no } = site;

    return (
        <Card sx={{ mb: 3, padding: "0px" }}>
            <CardActionArea>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                    >
                        {technician_entry.submitted && (
                            <Box
                                onClick={() => {
                                    if (allowTicketSelect) {
                                        navigate(`/tech-dash/${technician_entry.id}`);
                                    }
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="green"
                                    sx={{
                                        marginTop: "2px",
                                    }}
                                >
                                    SUBMITTED
                                </Typography>
                            </Box>
                        )}
                        {!technician_entry.submitted && (
                            <Box
                                onClick={() => {
                                    if (allowTicketSelect) {
                                        navigate(`/tech-dash/${technician_entry.id}`);
                                    }
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="orange"
                                    sx={{
                                        marginTop: "2px",
                                    }}
                                >
                                    IN PROGRESS
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                            }}
                        >
                            <Tooltip title="Get Directions using Google Maps">
                                <Link href={getGoogleMapsURL(site)} target="_blank">
                                    <DirectionsIcon />
                                </Link>
                            </Tooltip>

                            {comments && (
                                <Box
                                    sx={{
                                        zIndex: 1000,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "start",
                                    }}
                                >
                                    <Tooltip title="Notes from the Office">
                                        <IconButton
                                            color="error"
                                            sx={{
                                                paddingTop: "0px",
                                                paddingRight: "0px",
                                            }}
                                            onClick={() => {
                                                setSiteCommentsModalOpen(true);
                                                disableTicketSelect();
                                            }}
                                        >
                                            <FlagIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                        onClick={() => {
                            if (allowTicketSelect) {
                                navigate(`/tech-dash/${technician_entry.id}`);
                            }
                        }}
                    >
                        <Box>
                            <Typography gutterBottom variant="body1" component="div" sx={{ width: "100%" }}>
                                <b>{store_no}</b>
                                {` ${site.city}, ${site.state} WO`}{" "}
                                {work_order.ticket_no.length > 4
                                    ? work_order.ticket_no.substring(0, work_order.ticket_no.length - 4)
                                    : work_order.ticket_no}
                                <u>
                                    {work_order.ticket_no.length > 4
                                        ? work_order.ticket_no.substring(
                                              work_order.ticket_no.length - 4,
                                              work_order.ticket_no.length
                                          )
                                        : ""}
                                </u>
                            </Typography>
                            <Typography></Typography>
                            <Typography variant="body2" color="text.secondary">
                                {work_order.problem_description}
                            </Typography>
                        </Box>

                        <InfoModal
                            title={"Notes from the Office"}
                            description={comments ?? "--"}
                            open={siteCommentsModalOpen}
                            handleClose={() => {
                                setSiteCommentsModalOpen(false);
                                enableTicketSelect();
                            }}
                        />
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default TechnicianAssignmentCard;
